import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Package, Plus, Trash2, ChevronUp, ChevronDown, Save, X, RotateCcw,
    Sparkles, RefreshCw, AlertCircle, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminCatalog, createPackage, updatePackage, deletePackage, reorderPackages,
    createAddon, updateAddon, deleteAddon, resetCatalog,
} from "@/lib/adminApi";
import { useCatalog } from "@/lib/CatalogProvider";

const KIND_TABS = [
    { id: "logo", label: "Logo Packages", icon: Sparkles, kind: "logo", category: "Logo Design", addonsCategory: "branding" },
    { id: "branding", label: "Branding", icon: Package, kind: "branding", category: "Branding", addonsCategory: "branding" },
];

const ACCENT_OPTIONS = ["default", "flagship", "apex"];

export default function AdminPackages() {
    const { refresh: refreshPublic } = useCatalog();
    const [packages, setPackages] = useState([]);
    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("branding");
    const [editorPkg, setEditorPkg] = useState(null);
    const [editorAddon, setEditorAddon] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminCatalog();
            setPackages((data.packages || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
            setAddons((data.addons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
        } catch (_err) {
            toast.error("Could not load catalog");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const activeConfig = KIND_TABS.find((t) => t.id === activeTab);

    const visiblePackages = useMemo(
        () => packages.filter((p) => p.kind === activeConfig.kind).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
        [packages, activeConfig]
    );

    const visibleAddons = useMemo(
        () => addons.filter((a) => a.category === activeConfig.addonsCategory).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
        [addons, activeConfig]
    );

    const onCreatePackage = () => {
        const newPkg = {
            name: "New Package",
            category: activeConfig.category,
            kind: activeConfig.kind,
            tier: visiblePackages.length + 1,
            base_price: 0,
            price_label: "$0",
            price_note: "",
            description: "",
            features: [],
            ribbon: null,
            cta: "Choose Package",
            addons_category: activeConfig.addonsCategory,
            order: packages.length,
            visible: true,
            accent_style: "default",
        };
        setEditorPkg({ mode: "create", pkg: newPkg });
    };

    const onCreateAddon = (preFillPackageIds = []) => {
        const newAddon = {
            name: "New Add-on",
            description: "",
            price: 0,
            category: activeConfig.addonsCategory,
            applies_to_package_ids: Array.isArray(preFillPackageIds) ? preFillPackageIds : [],
            order: addons.length,
            visible: true,
        };
        setEditorAddon({ mode: "create", addon: newAddon });
    };

    const savePackage = async (form) => {
        try {
            if (editorPkg.mode === "create") {
                const created = await createPackage(form);
                setPackages((arr) => [...arr, created].sort((a, b) => (a.order || 0) - (b.order || 0)));
                toast.success("Package created");
            } else {
                const updated = await updatePackage(editorPkg.pkg.id, form);
                setPackages((arr) => arr.map((p) => (p.id === editorPkg.pkg.id ? updated : p)));
                toast.success("Package updated");
            }
            setEditorPkg(null);
            refreshPublic();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Save failed", { description: typeof detail === "string" ? detail : "Try again." });
        }
    };

    const saveAddon = async (form) => {
        try {
            if (editorAddon.mode === "create") {
                const created = await createAddon(form);
                setAddons((arr) => [...arr, created]);
                toast.success("Add-on created");
            } else {
                const updated = await updateAddon(editorAddon.addon.id, form);
                setAddons((arr) => arr.map((a) => (a.id === editorAddon.addon.id ? updated : a)));
                toast.success("Add-on updated");
            }
            setEditorAddon(null);
            refreshPublic();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Save failed", { description: typeof detail === "string" ? detail : "Try again." });
        }
    };

    const removePackage = async (pkg) => {
        if (!window.confirm(`Delete "${pkg.name}"? This cannot be undone.`)) return;
        try {
            await deletePackage(pkg.id);
            setPackages((arr) => arr.filter((p) => p.id !== pkg.id));
            toast.success("Package deleted");
            refreshPublic();
        } catch (_err) {
            toast.error("Delete failed");
        }
    };

    const removeAddon = async (addon) => {
        if (!window.confirm(`Delete "${addon.name}"?`)) return;
        try {
            await deleteAddon(addon.id);
            setAddons((arr) => arr.filter((a) => a.id !== addon.id));
            toast.success("Add-on deleted");
            refreshPublic();
        } catch (_err) {
            toast.error("Delete failed");
        }
    };

    const toggleVisibility = async (pkg) => {
        try {
            const updated = await updatePackage(pkg.id, { visible: !pkg.visible });
            setPackages((arr) => arr.map((p) => (p.id === pkg.id ? updated : p)));
            refreshPublic();
        } catch (_err) {
            toast.error("Could not toggle");
        }
    };

    const toggleAddonVisibility = async (addon) => {
        try {
            const updated = await updateAddon(addon.id, { visible: !addon.visible });
            setAddons((arr) => arr.map((a) => (a.id === addon.id ? updated : a)));
            refreshPublic();
        } catch (_err) {
            toast.error("Could not toggle");
        }
    };

    const reorder = async (pkg, dir) => {
        const list = visiblePackages;
        const idx = list.findIndex((p) => p.id === pkg.id);
        const target = idx + dir;
        if (target < 0 || target >= list.length) return;
        const newList = [...list];
        [newList[idx], newList[target]] = [newList[target], newList[idx]];
        // Apply new order values
        const updated = newList.map((p, i) => ({ ...p, order: i }));
        setPackages((arr) =>
            arr.map((p) => {
                const u = updated.find((x) => x.id === p.id);
                return u || p;
            })
        );
        try {
            await reorderPackages(updated.map((p) => p.id));
            refreshPublic();
        } catch (_err) {
            toast.error("Reorder failed");
            load();
        }
    };

    const onResetAll = async () => {
        if (!window.confirm("Restore all packages and add-ons to defaults? Your custom edits will be lost.")) return;
        try {
            await resetCatalog();
            await load();
            refreshPublic();
            toast.success("Reset to defaults");
        } catch (_err) {
            toast.error("Reset failed");
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <Package className="h-3.5 w-3.5" /> Studio CMS · Catalog
                    </span>
                    <h1
                        className="font-display mt-4 text-3xl sm:text-4xl"
                        style={{ color: "var(--pg-fg)" }}
                        data-testid="admin-packages-headline"
                    >
                        Packages & Add-Ons
                    </h1>
                    <p className="mt-2 max-w-xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Fully editable. Changes go live instantly on the public site.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-packages-refresh"
                    >
                        <RefreshCw className="h-4 w-4" /> Refresh
                    </button>
                    <button
                        type="button"
                        onClick={onResetAll}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-packages-reset"
                    >
                        <RotateCcw className="h-4 w-4" /> Reset Defaults
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="mt-6 inline-flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--pg-border)" }}>
                {KIND_TABS.map((t) => {
                    const active = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActiveTab(t.id)}
                            data-testid={`admin-packages-tab-${t.id}`}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] transition-colors"
                            style={{
                                background: active ? "var(--pg-fg)" : "transparent",
                                color: active ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                            }}
                        >
                            <t.icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Packages list */}
            <section className="mt-7" data-testid="admin-packages-section">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-display text-xl" style={{ color: "var(--pg-fg)" }}>
                        {activeConfig.label}
                    </h2>
                    <button
                        type="button"
                        onClick={onCreatePackage}
                        className="pg-btn-primary pg-app-tap"
                        data-testid="admin-packages-new"
                    >
                        <Plus className="h-4 w-4" /> New Package
                    </button>
                </div>
                {loading ? (
                    <div className="rounded-2xl border px-6 py-16 text-center text-sm" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>Loading…</div>
                ) : visiblePackages.length === 0 ? (
                    <div className="rounded-2xl border px-6 py-16 text-center text-sm" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        No packages yet. Click &quot;New Package&quot; to add one.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="admin-packages-list">
                        {visiblePackages.map((p, idx) => (
                            <PackageRow
                                key={p.id}
                                pkg={p}
                                index={idx}
                                total={visiblePackages.length}
                                onEdit={() => setEditorPkg({ mode: "edit", pkg: p })}
                                onDelete={() => removePackage(p)}
                                onToggleVisibility={() => toggleVisibility(p)}
                                onMoveUp={() => reorder(p, -1)}
                                onMoveDown={() => reorder(p, 1)}
                            />
                        ))}
                    </ul>
                )}
            </section>

            {/* Add-ons \u2014 per-package + global */}
            <section className="mt-12" data-testid="admin-addons-section">
                <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h2 className="font-display text-xl" style={{ color: "var(--pg-fg)" }}>
                            Branding Add-Ons
                        </h2>
                        <p className="mt-1 text-xs" style={{ color: "var(--pg-fg-muted)" }}>
                            Each package below shows the add-ons your clients will see when they pick that package.
                            <strong style={{ color: "var(--pg-fg)" }}> Global Add-Ons</strong> at the bottom apply to every package in this category.
                        </p>
                    </div>
                </div>

                {loading ? null : (
                    <>
                        {/* Per-package add-ons cards */}
                        <div className="space-y-5" data-testid="admin-addons-per-package">
                            {visiblePackages.map((pkg) => {
                                const own = visibleAddons.filter(
                                    (a) => Array.isArray(a.applies_to_package_ids) && a.applies_to_package_ids.includes(pkg.id)
                                );
                                const global = visibleAddons.filter(
                                    (a) => !a.applies_to_package_ids || a.applies_to_package_ids.length === 0
                                );
                                return (
                                    <div
                                        key={pkg.id}
                                        className="rounded-3xl border p-5 sm:p-6"
                                        style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                                        data-testid={`admin-addons-pkg-${pkg.id}`}
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                                    Add-Ons for
                                                </span>
                                                <h3 className="font-display text-lg" style={{ color: "var(--pg-fg)" }}>
                                                    {pkg.name}
                                                    <span className="ml-2 text-[12px] font-mono uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
                                                        \u00b7 {own.length} scoped \u00b7 {global.length} global
                                                    </span>
                                                </h3>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => onCreateAddon([pkg.id])}
                                                className="pg-btn-ghost pg-app-tap"
                                                data-testid={`admin-addons-new-for-${pkg.id}`}
                                            >
                                                <Plus className="h-3.5 w-3.5" /> Add Add-On to {pkg.name}
                                            </button>
                                        </div>

                                        {own.length === 0 && global.length === 0 ? (
                                            <div className="mt-3 rounded-xl border border-dashed px-4 py-6 text-center text-xs" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                                                No add-ons assigned. Use the button above to add one for this package only.
                                            </div>
                                        ) : (
                                            <ul className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                                                {own.map((a) => (
                                                    <AddonRow
                                                        key={`${pkg.id}-${a.id}`}
                                                        addon={a}
                                                        scope="specific"
                                                        onEdit={() => setEditorAddon({ mode: "edit", addon: a })}
                                                        onDelete={() => removeAddon(a)}
                                                        onToggleVisibility={() => toggleAddonVisibility(a)}
                                                    />
                                                ))}
                                                {global.map((a) => (
                                                    <AddonRow
                                                        key={`${pkg.id}-g-${a.id}`}
                                                        addon={a}
                                                        scope="global"
                                                        onEdit={() => setEditorAddon({ mode: "edit", addon: a })}
                                                        onDelete={() => removeAddon(a)}
                                                        onToggleVisibility={() => toggleAddonVisibility(a)}
                                                    />
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}

                            {visiblePackages.length === 0 && (
                                <div className="rounded-2xl border px-6 py-12 text-center text-sm" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                                    Add a package above to start assigning per-package add-ons.
                                </div>
                            )}
                        </div>

                        {/* Global add-ons management */}
                        <div className="mt-8" data-testid="admin-addons-global-section">
                            <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                                <div>
                                    <h3 className="font-display text-lg" style={{ color: "var(--pg-fg)" }}>
                                        Global Add-Ons
                                        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                            Applies to every {activeConfig.addonsCategory} package
                                        </span>
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onCreateAddon([])}
                                    className="pg-btn-primary pg-app-tap"
                                    data-testid="admin-addons-new-global"
                                >
                                    <Plus className="h-4 w-4" /> New Global Add-On
                                </button>
                            </div>
                            {(() => {
                                const global = visibleAddons.filter(
                                    (a) => !a.applies_to_package_ids || a.applies_to_package_ids.length === 0
                                );
                                if (global.length === 0) {
                                    return (
                                        <div className="rounded-2xl border px-6 py-10 text-center text-sm" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                                            No global add-ons. All add-ons currently scope to specific packages.
                                        </div>
                                    );
                                }
                                return (
                                    <ul className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="admin-addons-global-list">
                                        {global.map((a) => (
                                            <AddonRow
                                                key={a.id}
                                                addon={a}
                                                scope="global"
                                                onEdit={() => setEditorAddon({ mode: "edit", addon: a })}
                                                onDelete={() => removeAddon(a)}
                                                onToggleVisibility={() => toggleAddonVisibility(a)}
                                            />
                                        ))}
                                    </ul>
                                );
                            })()}
                        </div>
                    </>
                )}
            </section>

            <AnimatePresence>
                {editorPkg && (
                    <PackageEditor
                        state={editorPkg}
                        onClose={() => setEditorPkg(null)}
                        onSave={savePackage}
                    />
                )}
                {editorAddon && (
                    <AddonEditor
                        state={editorAddon}
                        packages={packages}
                        onClose={() => setEditorAddon(null)}
                        onSave={saveAddon}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// ---------------- Rows ----------------

const PackageRow = ({ pkg, index, total, onEdit, onDelete, onToggleVisibility, onMoveUp, onMoveDown }) => (
    <motion.li
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="pg-app-surface grid grid-cols-1 items-center gap-3 p-4 sm:grid-cols-[40px_1fr_auto] sm:p-5"
        data-testid="admin-package-row"
        data-package-id={pkg.id}
        style={{ opacity: pkg.visible ? 1 : 0.55 }}
    >
        <div className="flex flex-col items-center gap-1">
            <button type="button" onClick={onMoveUp} disabled={index === 0} aria-label="Move up" className="rounded p-1 disabled:opacity-30 hover:bg-[var(--pg-surface-2)]">
                <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onMoveDown} disabled={index === total - 1} aria-label="Move down" className="rounded p-1 disabled:opacity-30 hover:bg-[var(--pg-surface-2)]">
                <ChevronDown className="h-3.5 w-3.5" />
            </button>
        </div>
        <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-display text-lg leading-tight" style={{ color: "var(--pg-fg)" }}>{pkg.name}</span>
                {pkg.ribbon && (
                    <span className="inline-flex items-center rounded-full bg-[var(--pg-accent)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "#071112" }}>
                        {pkg.ribbon}
                    </span>
                )}
                {!pkg.visible && (
                    <span className="inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        Hidden
                    </span>
                )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                <span>Tier {pkg.tier}</span>
                <span>·</span>
                <span>{pkg.category}</span>
                <span>·</span>
                <span className="pg-price-num pg-text-gold-static text-base" data-testid="admin-package-price-display">{pkg.price_label || `$${Math.round(pkg.base_price || 0).toLocaleString()}`}</span>
                {pkg.features?.length > 0 && (
                    <>
                        <span>·</span>
                        <span>{pkg.features.length} features</span>
                    </>
                )}
            </div>
        </div>
        <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onToggleVisibility} className="pg-app-tap inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em]" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }} data-testid="admin-package-toggle-visibility">
                {pkg.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {pkg.visible ? "Live" : "Hidden"}
            </button>
            <button type="button" onClick={onEdit} className="pg-btn-primary pg-app-tap text-xs" data-testid="admin-package-edit">Edit</button>
            <button type="button" onClick={onDelete} aria-label="Delete" className="pg-app-tap inline-flex h-9 w-9 items-center justify-center rounded-full border" style={{ borderColor: "var(--pg-border)", color: "#E55353" }} data-testid="admin-package-delete">
                <Trash2 className="h-3.5 w-3.5" />
            </button>
        </div>
    </motion.li>
);

const AddonRow = ({ addon, scope, onEdit, onDelete, onToggleVisibility }) => (
    <motion.li
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="pg-app-surface flex items-center justify-between gap-3 p-4"
        data-testid="admin-addon-row"
        data-addon-id={addon.id}
        data-scope={scope || "any"}
        style={{ opacity: addon.visible ? 1 : 0.55 }}
    >
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate" style={{ color: "var(--pg-fg)" }}>{addon.name}</span>
                {scope === "global" && (
                    <span
                        className="rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]"
                        style={{
                            borderColor: "color-mix(in srgb, var(--pg-accent) 38%, transparent)",
                            background: "color-mix(in srgb, var(--pg-accent) 12%, transparent)",
                            color: "var(--pg-fg)",
                        }}
                        title="Applies to every package in this category"
                    >
                        Global
                    </span>
                )}
                {scope === "specific" && (
                    <span
                        className="rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]"
                        style={{
                            borderColor: "var(--pg-border-strong)",
                            background: "var(--pg-surface-2)",
                            color: "var(--pg-fg-muted)",
                        }}
                        title="Scoped to this package only"
                    >
                        Scoped
                    </span>
                )}
                {!addon.visible && (
                    <span className="rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>Hidden</span>
                )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                <span className="pg-price-num pg-text-gold-static text-sm">+${Math.round(addon.price || 0).toLocaleString()}</span>
                {addon.description && <><span>\u00b7</span><span className="truncate max-w-xs">{addon.description}</span></>}
            </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={onToggleVisibility} aria-label="Toggle visibility" className="rounded-full border p-1.5" style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }} data-testid="admin-addon-toggle-visibility">
                {addon.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            </button>
            <button type="button" onClick={onEdit} className="pg-btn-ghost pg-app-tap text-xs" data-testid="admin-addon-edit">Edit</button>
            <button type="button" onClick={onDelete} aria-label="Delete addon" className="inline-flex h-8 w-8 items-center justify-center rounded-full border" style={{ borderColor: "var(--pg-border)", color: "#E55353" }} data-testid="admin-addon-delete">
                <Trash2 className="h-3 w-3" />
            </button>
        </div>
    </motion.li>
);

// ---------------- Editors ----------------

const PackageEditor = ({ state, onClose, onSave }) => {
    const [form, setForm] = useState(state.pkg);
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setFeature = (idx, v) => {
        const features = [...(form.features || [])];
        features[idx] = v;
        setField("features", features);
    };
    const addFeature = () => setField("features", [...(form.features || []), ""]);
    const removeFeature = (idx) => setField("features", (form.features || []).filter((_, i) => i !== idx));

    const submit = (ev) => {
        ev.preventDefault();
        // Coerce numeric fields
        const out = {
            ...form,
            base_price: Number(form.base_price || 0),
            tier: Number(form.tier || 1),
            order: Number(form.order || 0),
            features: (form.features || []).filter((f) => f && f.trim()),
        };
        onSave(out);
    };

    return (
        <DrawerShell title={state.mode === "create" ? "New Package" : "Edit Package"} onClose={onClose} testId="admin-package-editor">
            <form onSubmit={submit} className="space-y-5">
                <FieldText label="Name" value={form.name} onChange={(v) => setField("name", v)} testId="admin-package-name" />
                <FieldText label="Category" value={form.category} onChange={(v) => setField("category", v)} placeholder="Logo Design | Branding" testId="admin-package-category" />
                <div className="grid grid-cols-2 gap-3">
                    <FieldText label="Tier (number)" value={form.tier} onChange={(v) => setField("tier", v)} testId="admin-package-tier" />
                    <FieldText label="Order" value={form.order} onChange={(v) => setField("order", v)} testId="admin-package-order" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <FieldText label="Base Price (numeric)" value={form.base_price} onChange={(v) => setField("base_price", v)} placeholder="1499" testId="admin-package-base-price" />
                    <FieldText label="Price Label (display)" value={form.price_label} onChange={(v) => setField("price_label", v)} placeholder="$1,499" testId="admin-package-price-label" />
                </div>
                <FieldText label="Price Note" value={form.price_note} onChange={(v) => setField("price_note", v)} placeholder="Starting From / per project" testId="admin-package-price-note" />
                <FieldText label="Ribbon (badge)" value={form.ribbon} onChange={(v) => setField("ribbon", v)} placeholder="Most Popular / Flagship" testId="admin-package-ribbon" />
                <FieldText label="CTA Button Label" value={form.cta} onChange={(v) => setField("cta", v)} placeholder="Choose Package" testId="admin-package-cta" />
                <div className="flex flex-col gap-1.5">
                    <SubLabel>Accent Style</SubLabel>
                    <select
                        value={form.accent_style || "default"}
                        onChange={(e) => setField("accent_style", e.target.value)}
                        className="pg-input"
                        data-testid="admin-package-accent"
                    >
                        {ACCENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <p className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                        default · flagship (gold accent) · apex (glass-morphism Crown) · cool-flagship (cyan flagship)
                    </p>
                </div>
                <FieldTextarea label="Description" value={form.description} onChange={(v) => setField("description", v)} testId="admin-package-description" />
                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <SubLabel>Features</SubLabel>
                        <button type="button" onClick={addFeature} className="pg-btn-ghost pg-app-tap" data-testid="admin-package-feature-add">
                            <Plus className="h-3.5 w-3.5" /> Add Feature
                        </button>
                    </div>
                    <ul className="space-y-2" data-testid="admin-package-features">
                        {(form.features || []).map((f, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={f}
                                    onChange={(e) => setFeature(idx, e.target.value)}
                                    className="pg-input flex-1"
                                    data-testid={`admin-package-feature-${idx}`}
                                />
                                <IconBtn onClick={() => removeFeature(idx)} aria-label="Remove feature" testId={`admin-package-feature-remove-${idx}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </IconBtn>
                            </li>
                        ))}
                    </ul>
                </div>
                <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--pg-fg)" }}>
                    <input type="checkbox" checked={!!form.visible} onChange={(e) => setField("visible", e.target.checked)} data-testid="admin-package-visible" />
                    Visible on public site
                </label>
                <div className="sticky bottom-0 -mx-5 mt-2 border-t px-5 py-4 sm:-mx-7 sm:px-7" style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}>
                    <button type="submit" className="pg-btn-primary w-full" data-testid="admin-package-save">
                        <Save className="h-4 w-4" /> {state.mode === "create" ? "Create Package" : "Save Changes"}
                    </button>
                </div>
            </form>
        </DrawerShell>
    );
};

const AddonEditor = ({ state, packages, onClose, onSave }) => {
    const [form, setForm] = useState(state.addon);
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const sameCategoryPkgs = (packages || []).filter((p) =>
        (p.addons_category || "branding") === form.category
    );

    const togglePkgLink = (id) => {
        const current = form.applies_to_package_ids || [];
        const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
        setField("applies_to_package_ids", next);
    };

    const submit = (ev) => {
        ev.preventDefault();
        onSave({ ...form, price: Number(form.price || 0), order: Number(form.order || 0) });
    };

    return (
        <DrawerShell title={state.mode === "create" ? "New Add-on" : "Edit Add-on"} onClose={onClose} testId="admin-addon-editor">
            <form onSubmit={submit} className="space-y-5">
                <FieldText label="Name" value={form.name} onChange={(v) => setField("name", v)} testId="admin-addon-name" />
                <FieldTextarea label="Description" value={form.description || ""} onChange={(v) => setField("description", v)} testId="admin-addon-description" />
                <div className="grid grid-cols-2 gap-3">
                    <FieldText label="Price (numeric)" value={form.price} onChange={(v) => setField("price", v)} placeholder="199" testId="admin-addon-price" />
                    <FieldText label="Order" value={form.order} onChange={(v) => setField("order", v)} testId="admin-addon-order" />
                </div>
                <div className="flex flex-col gap-1.5">
                    <SubLabel>Category</SubLabel>
                    <select
                        value={form.category}
                        onChange={(e) => setField("category", e.target.value)}
                        className="pg-input"
                        data-testid="admin-addon-category"
                    >
                        <option value="branding">Branding</option>
                    </select>
                </div>
                <div className="flex flex-col gap-2">
                    <SubLabel>Applies to specific packages (leave empty for all in category)</SubLabel>
                    <div className="flex flex-wrap gap-2" data-testid="admin-addon-applies">
                        {sameCategoryPkgs.length === 0 ? (
                            <span className="text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                                No packages in this category yet.
                            </span>
                        ) : sameCategoryPkgs.map((p) => {
                            const active = (form.applies_to_package_ids || []).includes(p.id);
                            return (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => togglePkgLink(p.id)}
                                    className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs"
                                    style={{
                                        borderColor: active ? "var(--pg-accent)" : "var(--pg-border)",
                                        background: active ? "color-mix(in srgb, var(--pg-accent) 15%, transparent)" : "var(--pg-surface)",
                                        color: "var(--pg-fg)",
                                    }}
                                >
                                    {p.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <label className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--pg-fg)" }}>
                    <input type="checkbox" checked={!!form.visible} onChange={(e) => setField("visible", e.target.checked)} data-testid="admin-addon-visible" />
                    Visible on public site
                </label>
                <div className="sticky bottom-0 -mx-5 mt-2 border-t px-5 py-4 sm:-mx-7 sm:px-7" style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}>
                    <button type="submit" className="pg-btn-primary w-full" data-testid="admin-addon-save">
                        <Save className="h-4 w-4" /> {state.mode === "create" ? "Create Add-on" : "Save Changes"}
                    </button>
                </div>
            </form>
        </DrawerShell>
    );
};

// ---------------- Shared UI ----------------

const DrawerShell = ({ title, onClose, children, testId }) => (
    <motion.div
        className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid={testId}
    >
        <motion.aside
            onClick={(e) => e.stopPropagation()}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full flex-col overflow-y-auto sm:w-[560px]"
            style={{ background: "var(--pg-bg)", borderLeft: "1px solid var(--pg-border)" }}
        >
            <div className="flex items-center justify-between gap-3 border-b px-5 py-5 sm:px-7" style={{ borderColor: "var(--pg-border)" }}>
                <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>{title}</h2>
                <button onClick={onClose} aria-label="Close" className="rounded-full p-2" style={{ color: "var(--pg-fg-muted)" }}>
                    <X className="h-4 w-4" />
                </button>
            </div>
            <div className="flex-1 px-5 py-6 sm:px-7">{children}</div>
        </motion.aside>
    </motion.div>
);

const SubLabel = ({ children }) => (
    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
        {children}
    </span>
);

const FieldText = ({ label, value, onChange, placeholder, testId }) => (
    <div className="flex flex-col gap-1.5">
        <SubLabel>{label}</SubLabel>
        <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || ""}
            className="pg-input"
            data-testid={testId}
        />
    </div>
);

const FieldTextarea = ({ label, value, onChange, testId }) => (
    <div className="flex flex-col gap-1.5">
        <SubLabel>{label}</SubLabel>
        <textarea
            rows={3}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="pg-input"
            style={{ resize: "vertical", minHeight: 90 }}
            data-testid={testId}
        />
    </div>
);

const IconBtn = ({ onClick, children, "aria-label": ariaLabel, testId }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-[var(--pg-surface-2)]"
        style={{ borderColor: "var(--pg-border)", color: "#E55353" }}
    >
        {children}
    </button>
);
